import React from 'react';

/**
 * Verified subscription state — derived once on the backend by
 * services/subscription_verifier.py and shipped as user.subscription.verifiedState.
 *
 * One mutually exclusive bucket per user, plus suffix fields that the pill
 * uses to disclose context (trial cancelled before expiry, comp layered on
 * top of a real paying sub, etc.). Replaces the old badge stack that let
 * beta testers render green "Paying" tiles indistinguishable from revenue.
 */
export interface VerifiedState {
  bucket:
    | 'paying'
    | 'paying_cancelled'
    | 'billing_issue'
    | 'pre_trial'
    | 'trial'
    | 'trial_expired'
    | 'sub_expired'
    | 'comp_beta'
    | 'comp_override'
    | 'comp_evaluation'
    // 🌞 Summer50: a self-serve campaign free month, and its churn state. Kept
    // OUT of the generic comp buckets — a founding member is a campaign
    // acquisition, not an admin grant, and a lapsed free month is not a lost
    // trial. Both were previously invisible (rendered as "Comp").
    | 'comp_free_month'
    | 'free_churned'
    | 'none';
  tierLabel: string | null;
  endsAt: string | null;
  cancelledDuringTrial: boolean;
  compReason: 'evaluation' | 'beta_tester' | 'admin_override' | null;
  underlying?: {
    bucket: VerifiedState['bucket'];
    tierLabel: string | null;
    endsAt: string | null;
  } | null;
  alsoPaying: boolean;
}

interface Props {
  state?: VerifiedState | null;
  /**
   * v41 (2026-06-08): compact mode renders just the verdict word
   * ('Paying', 'Trial', 'Comp', 'Churned', 'Pre-trial') without the
   * tier suffix or date qualifiers. Used by the /admin/users table
   * to keep the Pays? column narrow. Tier still shows in the user
   * detail modal.
   */
  compact?: boolean;
}

const fmtDate = (iso: string | null): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  } catch {
    return '';
  }
};

// Tailwind class string per bucket. Kept inline (no helper map keyed by bucket)
// so each branch's intent is grep-able and the color choices are visible at the
// site they apply.
const PILL_BASE = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap';

// "Underlying" suffix shown on comp pills. Tells the admin what the user's
// real (pre-comp) state is — so a beta tester who's also paying Essentials
// doesn't get hidden behind a teal "Comp · Pro (beta)" tile.
const underlyingSuffix = (underlying: VerifiedState['underlying']): string | null => {
  if (!underlying || underlying.bucket === 'none') return null;
  const tier = underlying.tierLabel || '';
  switch (underlying.bucket) {
    case 'paying':
      return tier ? `also paying ${tier}` : 'also paying';
    case 'paying_cancelled':
      return tier ? `paying ${tier} (cancelled)` : 'paying (cancelled)';
    case 'billing_issue':
      return tier ? `${tier} in grace` : 'in grace';
    case 'pre_trial':
      return 'underlying: pre-trial';
    case 'trial':
      return 'underlying: trial';
    case 'trial_expired':
      return 'underlying: trial expired';
    case 'sub_expired':
      return tier ? `churned ${tier}` : 'churned';
    case 'comp_free_month':
      return 'underlying: free month';
    case 'free_churned':
      return 'underlying: free month ended';
    default:
      return null;
  }
};

export const VerifiedStatePill: React.FC<Props> = ({ state, compact = false }) => {
  if (!state) {
    return <span className="text-gray-400 text-xs">—</span>;
  }

  const { bucket, tierLabel, endsAt, cancelledDuringTrial, alsoPaying, underlying } = state;
  const dateStr = fmtDate(endsAt);

  // v41 (2026-06-08): compact mode short-circuits to a verdict-only
  // pill. Drops tier + dates + qualifiers so the admin /users Pays?
  // column stays narrow. Color/icon preserved per bucket so a quick
  // scan still surfaces the same signal.
  if (compact) {
    switch (bucket) {
      case 'paying':
      case 'paying_cancelled':
        return <span className={`${PILL_BASE} bg-green-100 text-green-800`}>✓ Paying</span>;
      case 'billing_issue':
        return <span className={`${PILL_BASE} bg-orange-100 text-orange-800`}>⚠ Billing</span>;
      case 'pre_trial':
        return <span className={`${PILL_BASE} bg-blue-50 text-blue-800 border border-blue-200`}>Pre-trial</span>;
      case 'trial':
        return <span className={`${PILL_BASE} bg-yellow-50 text-yellow-800 border border-yellow-200`}>Trial</span>;
      case 'trial_expired':
        return <span className={`${PILL_BASE} bg-gray-100 text-gray-600`}>Trial expired</span>;
      case 'sub_expired':
        return <span className={`${PILL_BASE} bg-red-50 text-red-700 border border-red-200`}>Churned</span>;
      case 'comp_beta':
      case 'comp_override':
      case 'comp_evaluation':
        return <span className={`${PILL_BASE} bg-teal-100 text-teal-800`}>Comp</span>;
      case 'comp_free_month':
        return <span className={`${PILL_BASE} bg-emerald-100 text-emerald-800`}>Free Month</span>;
      case 'free_churned':
        return <span className={`${PILL_BASE} bg-amber-50 text-amber-800 border border-amber-200`}>Free Churned</span>;
      default:
        return <span className={`${PILL_BASE} bg-gray-100 text-gray-700`}>—</span>;
    }
  }

  // Comp suffix is computed once and reused across all three comp branches.
  // alsoPaying is the most important admin signal (don't hide live revenue),
  // so it wins over the underlying-bucket descriptor.
  const compSuffix = alsoPaying
    ? underlyingSuffix(underlying)
    : underlyingSuffix(underlying);

  switch (bucket) {
    case 'paying':
      return (
        <span className={`${PILL_BASE} bg-green-100 text-green-800`}>
          ✓ Paying · {tierLabel}
        </span>
      );

    case 'paying_cancelled':
      // Amber: still paying through this billing period, but won't renew.
      // Date matters here — admin needs to see when the revenue stops.
      return (
        <span className={`${PILL_BASE} bg-amber-100 text-amber-800`}>
          ✓ Paying · {tierLabel} · cancels {dateStr}
        </span>
      );

    case 'billing_issue':
      // Apple/Google retry windows are 16–60 days; the user still has
      // access but the next renewal will fail unless they fix the card.
      return (
        <span className={`${PILL_BASE} bg-orange-100 text-orange-800`}>
          ⚠ Billing issue{dateStr ? ` · ends ${dateStr}` : ''}
        </span>
      );

    case 'comp_free_month':
      // 🌞 Summer50 founding member. Deliberately NOT the teal "Comp" chip:
      // this is a campaign acquisition on a clock, and the end date is the
      // whole point — it's when Paul needs to have earned the subscription.
      return (
        <span className={`${PILL_BASE} bg-emerald-100 text-emerald-800`}>
          🌲 Free Month{dateStr ? ` · ends ${dateStr}` : ''}
        </span>
      );

    case 'free_churned':
      // Free month ended without converting. Distinct from 'trial_expired':
      // these users never had a trial, so counting them as lost trials would
      // misstate both funnels.
      return (
        <span className={`${PILL_BASE} bg-amber-50 text-amber-800 border border-amber-200`}>
          Free Churned
        </span>
      );

    case 'pre_trial':
      // Signed up, hasn't tapped "Start Free Trial" yet — 7-day clock has
      // not begun. Distinct from 'trial' (clock running) so ops can tell at
      // a glance which signups are gate-eligible vs. actively trialing.
      // Blue: cold/waiting, no warning weight. See the Trial Gate Invariant
      // in CLAUDE.md.
      return (
        <span className={`${PILL_BASE} bg-blue-50 text-blue-800 border border-blue-200`}>
          Pre-trial
        </span>
      );

    case 'trial':
      return (
        <span className={`${PILL_BASE} bg-yellow-50 text-yellow-800 border border-yellow-200`}>
          Trial{dateStr ? ` · ends ${dateStr}` : ''}
          {cancelledDuringTrial ? " · won't renew" : ''}
        </span>
      );

    case 'trial_expired':
      // Differentiate "let it lapse silently" from "actively cancelled" —
      // they're different user-intent signals even though the access state
      // is identical.
      return (
        <span className={`${PILL_BASE} bg-gray-100 text-gray-600`}>
          {cancelledDuringTrial ? 'Trial cancelled' : 'Trial expired'}
        </span>
      );

    case 'sub_expired':
      // Used to be paying, isn't anymore. Different visual weight than a
      // trial that never converted — these are churned revenue.
      return (
        <span className={`${PILL_BASE} bg-red-50 text-red-700 border border-red-200`}>
          {tierLabel ? `${tierLabel} churned` : 'Sub expired'}
        </span>
      );

    case 'comp_beta':
      return (
        <span className={`${PILL_BASE} bg-teal-100 text-teal-800`}>
          Comp · {tierLabel} (beta)
          {compSuffix ? ` · ${compSuffix}` : ''}
        </span>
      );

    case 'comp_override':
      return (
        <span className={`${PILL_BASE} bg-teal-100 text-teal-800`}>
          Comp · {tierLabel} (override)
          {compSuffix ? ` · ${compSuffix}` : ''}
        </span>
      );

    case 'comp_evaluation':
      return (
        <span className={`${PILL_BASE} bg-teal-100 text-teal-800`}>
          Comp · {tierLabel} (eval{dateStr ? `, ends ${dateStr}` : ''})
          {compSuffix ? ` · ${compSuffix}` : ''}
        </span>
      );

    case 'none':
    default:
      return <span className="text-gray-400 text-xs">—</span>;
  }
};

/**
 * Top-level filter buckets used by the admin user list's "Pays?" filter chips.
 * Maps each bucket to one of four groups; admins scan by money-flow first.
 */
export type PaysGroup = 'paying' | 'trial' | 'comp' | 'churned' | 'other';

export const paysGroupForBucket = (bucket: VerifiedState['bucket']): PaysGroup => {
  switch (bucket) {
    case 'paying':
    case 'paying_cancelled':
    case 'billing_issue':
      return 'paying';
    case 'pre_trial':
    case 'trial':
      // Pre-trial folds into the 'trial' Pays? chip — both are gate-eligible
      // signups that haven't paid yet. Separating them would split the
      // top-of-funnel view that admins scan first.
      return 'trial';
    case 'comp_beta':
    case 'comp_override':
    case 'comp_evaluation':
    case 'comp_free_month':
      return 'comp';
    case 'trial_expired':
    case 'sub_expired':
    case 'free_churned':
      return 'churned';
    case 'none':
    default:
      return 'other';
  }
};

/**
 * Sort priority for the "Pays?" column. Lower = appears first on ascending
 * sort. Order: real paying → trial → comp → churned → none. Makes a sorted
 * list show revenue at the top.
 */
export const paysSortRank = (bucket: VerifiedState['bucket']): number => {
  switch (bucket) {
    case 'paying': return 0;
    case 'paying_cancelled': return 1;
    case 'billing_issue': return 2;
    case 'trial': return 3;
    case 'pre_trial': return 4;       // top-of-funnel — between active trials and comp
    case 'comp_free_month': return 5;   // 🌞 campaign grants lead the comp block
    case 'comp_evaluation': return 6;
    case 'comp_beta': return 7;
    case 'comp_override': return 8;
    case 'sub_expired': return 9;
    case 'trial_expired': return 10;
    case 'free_churned': return 11;
    case 'none': return 12;
    default: return 13;
  }
};

export default VerifiedStatePill;
